
import React, { useEffect, useRef } from 'react';
import { Task } from '@/types/lead';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { Calendar, Check, Pencil, Trash2, User, Tag, ListChecks, Repeat } from 'lucide-react';
import { getTaskPriorityOptions } from '@/services/leads/taskService';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { confirm } from '@/components/ui/confirm';
import EditTaskDialog from '@/components/business/tasks/EditTaskDialog';
import { TaskWithLead } from '@/types/taskFilters';
import { useIsMobile } from '@/hooks/use-mobile';
import { stripHtml } from '@/lib/utils';

interface TasksOverviewProps {
  tasks: TaskWithLead[];
  type: 'pending' | 'overdue' | 'completed';
  onCompleteTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  highlightedTaskId?: string | null;
  selectedTasks?: Set<string>;
  onToggleSelect?: (taskId: string) => void;
  onSelectAll?: (taskIds: string[]) => void;
}

const TasksOverview: React.FC<TasksOverviewProps> = ({ 
  tasks, 
  type,
  onCompleteTask,
  onDeleteTask,
  onUpdateTask,
  highlightedTaskId,
  selectedTasks,
  onToggleSelect,
  onSelectAll,
}) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { data: companyMembers } = useCompanyMembers();
  const priorityOptions = getTaskPriorityOptions();
  const [editingTask, setEditingTask] = React.useState<Task | null>(null);
  
  const highlightedTaskRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightedTaskId && highlightedTaskRef.current) {
      highlightedTaskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedTaskId, tasks]);

  const getTaskStatusIndicator = (task: Task) => {
    if (task.status === 'completed') {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
          Completed
        </Badge>
      );
    }
    
    if (new Date(task.dueDate) < new Date()) {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
          Overdue
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
        Pending
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const option = priorityOptions.find(opt => opt.value === priority);
    if (!option) return null;
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const handleDeleteTask = (taskId: string) => {
    confirm({
      title: "Delete Task",
      description: "Are you sure you want to delete this task? This action cannot be undone.",
      onConfirm: () => onDeleteTask(taskId),
    });
  };

  const renderCategoryBadges = (categories?: string[]) => {
    if (!categories || categories.length === 0) return null;
    
    return (
      <>
        {categories.slice(0, 2).map((category) => (
          <Badge key={category} variant="outline" className="flex items-center gap-1 text-xs sm:text-sm bg-slate-100">
            <Tag className="h-3 w-3" />
            {category}
          </Badge>
        ))}
        {categories.length > 2 && (
          <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm bg-slate-100">
            +{categories.length - 2}
          </Badge>
        )}
      </>
    );
  };

  const allSelected = tasks.length > 0 && selectedTasks && tasks.every(t => selectedTasks.has(t.id));
  const someSelected = selectedTasks && tasks.some(t => selectedTasks.has(t.id));

  if (!tasks.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No {type} tasks found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onSelectAll && onToggleSelect && (
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            checked={allSelected}
            onCheckedChange={() => {
              if (allSelected) {
                onSelectAll([]);
              } else {
                onSelectAll(tasks.map(t => t.id));
              }
            }}
          />
          <span className="text-sm text-muted-foreground">
            {allSelected ? 'Deselect all' : 'Select all'} ({tasks.length})
          </span>
        </div>
      )}

      {tasks.map((task) => {
        const isHighlighted = task.id === highlightedTaskId;
        const isSelected = selectedTasks?.has(task.id) ?? false;
        
        return (
          <div 
            key={task.id} 
            className={`border rounded-lg p-4 transition-all duration-300 ${isHighlighted ? 'ring-2 ring-primary border-primary bg-primary/5' : ''} ${isSelected ? 'bg-accent/30 border-accent' : ''}`}
            ref={isHighlighted ? highlightedTaskRef : null}
          >
            <div className="flex gap-3">
              {onToggleSelect && (
                <div className="flex items-start pt-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggleSelect(task.id)}
                  />
                </div>
              )}
              
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full">
                  <div className="space-y-1 mb-3 md:mb-0">
                    <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                      <h4 className="font-medium">{task.title}</h4>
                      <Link 
                        to={`/business/${task.leads.id}`}
                        className="text-sm text-muted-foreground hover:underline truncate max-w-full md:max-w-xs"
                      >
                        {task.leads.business_name}
                      </Link>
                    </div>
                    {task.description && (
                      <p className="text-sm text-muted-foreground">{stripHtml(task.description)}</p>
                    )}
                  </div>
                  
                  {!isMobile && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="icon" onClick={() => setEditingTask(task)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {type !== 'completed' && (
                        <Button variant="outline" size="icon" onClick={() => onCompleteTask(task.id)}>
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="icon" onClick={() => handleDeleteTask(task.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {getPriorityBadge(task.priority)}
                  {task.dueDate && (
                    <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(task.dueDate), 'MMM d, yyyy')}
                    </Badge>
                  )}
                  {getTaskStatusIndicator(task)}
                  {renderCategoryBadges(task.categories)}
                  {task.checklist && task.checklist.length > 0 && (
                    <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
                      <ListChecks className="h-3 w-3" />
                      {task.checklist.filter(i => i.completed).length}/{task.checklist.length}
                    </Badge>
                    )}
                    {task.recurrencePattern && (
                      <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
                        <Repeat className="h-3 w-3" />
                        {task.recurrenceInterval && task.recurrenceInterval > 1 ? `${task.recurrenceInterval} ` : ''}{task.recurrencePattern}
                      </Badge>
                    )}
                    {task.assignee ? (
                    <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
                      <Avatar className="h-4 w-4">
                        <AvatarImage src={task.assignee.avatar_url || ''} />
                        <AvatarFallback>{`${task.assignee.first_name[0]}${task.assignee.last_name[0]}`}</AvatarFallback>
                      </Avatar>
                      <span className="truncate max-w-[80px] md:max-w-[120px]">{`${task.assignee.first_name} ${task.assignee.last_name}`}</span>
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1 text-xs sm:text-sm">
                      <User className="h-3 w-3" />
                      <span>Unassigned</span>
                    </Badge>
                  )}
                </div>
                
                {isMobile && (
                  <div className="flex justify-end gap-2 mt-3 border-t pt-3">
                    <Button variant="outline" size="icon" onClick={() => setEditingTask(task)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {type !== 'completed' && (
                      <Button variant="outline" size="icon" onClick={() => onCompleteTask(task.id)}>
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="icon" onClick={() => handleDeleteTask(task.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {editingTask && (
        <EditTaskDialog
          isOpen={!!editingTask}
          setIsOpen={() => setEditingTask(null)}
          task={editingTask}
          onSave={onUpdateTask}
          priorityOptions={priorityOptions}
        />
      )}
    </div>
  );
};

export default TasksOverview;
